import "./bannerForm.css";
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { useGetAllBannersQuery, useUpdateBannerMutation } from '../../services/bannerApi'
import { ViewCarouselOutlined } from "@mui/icons-material";
import { AdminPageHeader, AdminCard, AdminInput, FieldError, LoadingButton, AdminSkeleton, AdminEmptyState, useToast } from '../../components/ui'

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const BannerEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate();
  const { data: banners, isLoading: bannersLoading, isError } = useGetAllBannersQuery()
  const [updateBanner, { isLoading }] = useUpdateBannerMutation()
  const [server_error, setServerError] = useState({})
  const [formData, setFormData] = useState(null)
  const toast = useToast();

  const banner = banners?.find((b) => String(b.id) === id)

  useEffect(() => {
    if (banner) {
      setFormData({
        title: banner.title,
        subtitle: banner.subtitle,
        link_url: banner.link_url,
        sort_order: banner.sort_order,
        starts_at: toDatetimeLocal(banner.starts_at),
        ends_at: toDatetimeLocal(banner.ends_at),
        is_active: banner.is_active,
      })
    }
  }, [banner])

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const submitted = new FormData(event.currentTarget);
    const data = new FormData();
    data.append('title', formData.title || '');
    data.append('subtitle', formData.subtitle || '');
    data.append('link_url', formData.link_url || '');
    data.append('sort_order', formData.sort_order || 0);
    if (formData.starts_at) data.append('starts_at', formData.starts_at);
    if (formData.ends_at) data.append('ends_at', formData.ends_at);
    data.append('is_active', formData.is_active ? 'true' : 'false');
    const newImage = submitted.get('image');
    if (newImage && newImage.size > 0) {
      data.append('image', newImage);
    }

    const res = await updateBanner({ id, data });
    if (res.error) {
      setServerError(res.error.data?.errors || {});
      toast.error("Could not update this banner. Check the fields below.");
    }
    if (res.data) {
      toast.success("Banner updated successfully.");
      setTimeout(() => navigate('/admin/banners'), 1200);
    }
  };

  const breadcrumbs = [{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Banners", to: "/admin/banners" }, { label: "Edit" }];

  if (bannersLoading || !formData) {
    return (
      <div>
        <AdminPageHeader title="Edit Banner" breadcrumbs={breadcrumbs} />
        <AdminCard><AdminSkeleton variant="card" height={200} /></AdminCard>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Edit Banner" breadcrumbs={breadcrumbs} />
        <AdminEmptyState icon={<ViewCarouselOutlined />} title="Couldn't load this banner" description="Something went wrong. Try refreshing the page." />
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader title="Edit Banner" breadcrumbs={breadcrumbs} />
      <AdminCard className="bannerForm">
        <form onSubmit={handleSubmit}>
          {banner?.image && (
            <div className="admin-field">
              <span className="admin-field__label">Current Image</span>
              <img src={`${API_BASE}${banner.image}`} alt="" className="bannerForm__preview" />
            </div>
          )}
          <div className="admin-field">
            <label className="admin-field__label" htmlFor="image">Replace Image (optional)</label>
            <input id="image" type="file" name="image" accept="image/*" />
            <FieldError>{server_error.image?.[0]}</FieldError>
          </div>
          <AdminInput label="Title" name="title" value={formData.title || ""} onChange={handleChange} />
          <AdminInput label="Subtitle" name="subtitle" value={formData.subtitle || ""} onChange={handleChange} />
          <AdminInput label="Link URL" name="link_url" value={formData.link_url || ""} onChange={handleChange} />
          <AdminInput label="Sort Order" type="number" name="sort_order" value={formData.sort_order ?? 0} onChange={handleChange} />
          <AdminInput label="Starts At" type="datetime-local" name="starts_at" value={formData.starts_at || ""} onChange={handleChange} />
          <AdminInput label="Ends At" type="datetime-local" name="ends_at" value={formData.ends_at || ""} onChange={handleChange} />
          <label className="bannerForm__checkbox">
            <input type="checkbox" name="is_active" checked={!!formData.is_active} onChange={handleChange} /> Active
          </label>
          <div className="bannerForm__actions">
            <LoadingButton type="submit" loading={isLoading} loadingLabel="Saving...">Update Banner</LoadingButton>
          </div>
        </form>
      </AdminCard>
    </div>
  );
}

export default BannerEdit
